import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  Field,
  Metric,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  AuditProject,
  ComplianceFramework,
  FindingSweepResult,
  FrameworkVersion,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, auditApi, frameworksApi } from "../../lib/api";
import {
  ActionGate,
  GOVERNANCE_PERMISSIONS,
  GovPanel,
  GovernedAction,
  useGovernedMutation,
} from "../../governance";
import { PROJECT_STATUSES, ProjectStatusBadge, projectStatusLabel } from "./auditLabels";

interface Member {
  id: string;
  email: string;
  displayName: string;
}

// Audit projects (APP-004 R3 P5).
//
// An audit here is a governed record, not a workspace folder: its scope, its
// findings and its readiness position are all evidence of how the examination
// was run. Everything on this screen is read from the backend, including the
// lifecycle, so nothing claims a state the audit is not actually in.
export function GovAudits() {
  const [params, setParams] = useSearchParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const status = params.get("status") ?? "";
  const search = params.get("q") ?? "";
  const [sweep, setSweep] = useState<FindingSweepResult | null>(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const projects = useAsync<AuditProject[]>(
    async () => (await auditApi.projects(orgId)).data.auditProjects,
    [orgId],
  );
  const members = useAsync<Member[]>(async () => (await api.members(orgId)).members, [orgId]);
  const memberName = (id: string) => {
    const match = (members.data ?? []).find((m) => m.id === id);
    return match ? match.displayName || match.email : id;
  };

  const matches = (row: AuditProject) => {
    if (status && row.status !== status) return false;
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [row.name, row.auditorName, row.description].some((value) =>
      value.toLowerCase().includes(term),
    );
  };

  const rows = projects.data ?? [];
  const live = rows.filter((r) => r.status === "fieldwork" || r.status === "remediation").length;
  const planning = rows.filter((r) => r.status === "planning").length;
  const closed = rows.filter((r) => r.status === "closed").length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Audits</h1>
          <p>
            Examinations you are preparing for or answering, each with its own scope, findings and
            readiness position. An audit closes only once every finding is settled and a readiness
            snapshot has been frozen.
          </p>
        </div>
        <div className="badge-row">
          <GovernedAction
            permission={GOVERNANCE_PERMISSIONS.auditsManage}
            label="Run overdue sweep"
            title="Chase findings past their due date"
            description="Asks the backend to chase every finding whose remediation date has passed."
            consequence="A finding is chased once. Running the sweep again reports zero for anything already chased."
            confirmLabel="Run sweep"
            busyLabel="Running…"
            onConfirm={async () => {
              const res = await auditApi.runFindingSweep(orgId);
              setSweep(res.data);
              return res;
            }}
            onDone={projects.reload}
          />
          <CreateProject orgId={orgId} members={members.data ?? []} onDone={projects.reload} />
        </div>
      </div>

      {sweep ? (
        <Card>
          <CardHeader title="Last overdue sweep" />
          <CardBody>
            <div className="metric-grid">
              <div className="metric-card">
                <Metric value={sweep.due} label="Findings with a due date" />
              </div>
              <div className="metric-card">
                <Metric value={sweep.overdue} label="Chased this run" />
              </div>
            </div>
            <p className="muted-note">A repeat run reports zero for anything already chased.</p>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardBody>
          <div className="metric-grid" data-testid="audit-totals">
            <div className="metric-card">
              <Metric value={planning} label="In planning" />
            </div>
            <div className="metric-card">
              <Metric value={live} label="Fieldwork or remediation" />
            </div>
            <div className="metric-card">
              <Metric value={closed} label="Closed" />
            </div>
          </div>
          <div className="filter-row">
            <Field label="Search">
              {(id) => (
                <TextInput
                  id={id}
                  value={search}
                  placeholder="Audit or auditor"
                  onChange={(e) => setParam("q", e.target.value)}
                />
              )}
            </Field>
            <Field label="Status">
              {(id) => (
                <Select id={id} value={status} onChange={(e) => setParam("status", e.target.value)}>
                  <option value="">Any status</option>
                  {PROJECT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {projectStatusLabel(s)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </CardBody>
      </Card>

      <GovPanel
        title="Audit projects"
        state={projects}
        loadingLabel="Loading audits…"
        isEmpty={(data) => data.filter(matches).length === 0}
        emptyTitle="No audits match"
        emptyDescription="Create the examination you are preparing for, then scope it to the framework it covers."
      >
        {(data) => (
          <DataTable
            caption="Audit projects"
            columns={[
              {
                key: "name",
                header: "Audit",
                render: (row) => (
                  <>
                    <Link to={`/studio/governance/audits/${row.id}`}>{row.name}</Link>
                    <div className="badge-row">
                      <ProjectStatusBadge status={row.status} />
                    </div>
                    {row.description ? <div className="muted-note">{row.description}</div> : null}
                  </>
                ),
              },
              {
                key: "auditor",
                header: "Auditor",
                render: (row) =>
                  row.auditorName || <span className="muted-note">Not recorded</span>,
              },
              { key: "lead", header: "Lead", render: (row) => memberName(row.leadId) },
              {
                key: "period",
                header: "Period",
                render: (row) =>
                  row.periodStart || row.periodEnd ? (
                    <>
                      {row.periodStart ? formatDate(row.periodStart) : "Open start"} to{" "}
                      {row.periodEnd ? formatDate(row.periodEnd) : "Open end"}
                    </>
                  ) : (
                    <span className="muted-note">No period set</span>
                  ),
              },
              {
                key: "closed",
                header: "Closed",
                render: (row) =>
                  row.closedAt ? (
                    <>
                      {formatDate(row.closedAt)}
                      <div className="muted-note">by {memberName(row.closedBy ?? "")}</div>
                    </>
                  ) : (
                    <span className="muted-note">Still open</span>
                  ),
              },
            ]}
            rows={data.filter(matches)}
          />
        )}
      </GovPanel>
    </>
  );
}

function CreateProject({
  orgId,
  members,
  onDone,
}: {
  orgId: string;
  members: Member[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [auditorName, setAuditorName] = useState("");
  const [leadId, setLeadId] = useState("");
  const [frameworkVersionId, setFrameworkVersionId] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.auditsManage);

  const versions = useAsync<{ version: FrameworkVersion; framework: ComplianceFramework }[]>(
    async () => {
      if (!open) return [];
      const frameworks = (await frameworksApi.frameworks(orgId, { scope: "tenant" })).data
        .frameworks;
      return (
        await Promise.all(
          frameworks.map(async (framework) =>
            (await frameworksApi.versions(orgId, framework.id)).data.versions.map((version) => ({
              version,
              framework,
            })),
          ),
        )
      ).flat();
    },
    [orgId, open],
  );

  const lead = leadId || members[0]?.id || "";

  const submit = async () => {
    const ok = await mutation.run(() =>
      auditApi.createProject(orgId, {
        name: name.trim(),
        description: description.trim(),
        auditorName: auditorName.trim(),
        leadId: lead,
        frameworkVersionId: frameworkVersionId || null,
        periodStart: periodStart || null,
        periodEnd: periodEnd || null,
      }),
    );
    if (!ok) return;
    setOpen(false);
    setName("");
    setDescription("");
    setAuditorName("");
    setPeriodStart("");
    setPeriodEnd("");
    onDone();
  };

  return (
    <>
      <ActionGate permission={GOVERNANCE_PERMISSIONS.auditsManage}>
        <Button variant="primary" onClick={() => setOpen(true)}>
          Create audit
        </Button>
      </ActionGate>
      <Dialog
        open={open}
        title="Create an audit"
        description="The audit starts in planning. Scope it, then move it to fieldwork when the examination begins."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!name.trim() || !lead || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Creating…" : "Create audit"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Name">
          {(id) => (
            <TextInput
              id={id}
              value={name}
              placeholder="SOC 2 FY26 Type II"
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Description">
          {(id) => (
            <TextArea
              id={id}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <Field label="Auditor" help="The firm or examiner conducting the audit.">
          {(id) => (
            <TextInput
              id={id}
              value={auditorName}
              onChange={(e) => setAuditorName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Lead" help="Who answers for this audit inside your organization.">
          {(id) => (
            <Select id={id} value={lead} onChange={(e) => setLeadId(e.target.value)}>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName || m.email}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Framework version"
          help="Readiness and coverage are measured against this version."
        >
          {(id) => (
            <Select
              id={id}
              value={frameworkVersionId}
              onChange={(e) => setFrameworkVersionId(e.target.value)}
            >
              <option value="">No framework yet</option>
              {(versions.data ?? []).map(({ version, framework }) => (
                <option key={version.id} value={version.id}>
                  {framework.name} {version.versionLabel}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="filter-row">
          <Field label="Period start">
            {(id) => (
              <TextInput
                id={id}
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            )}
          </Field>
          <Field label="Period end">
            {(id) => (
              <TextInput
                id={id}
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            )}
          </Field>
        </div>
      </Dialog>
    </>
  );
}
